import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Steps } from 'primereact/steps';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { EstadoTag, TEMAS_ENTREVISTA, fechaHora, poli } from './poliApi';
import ArchivosPrueba from './ArchivosPrueba';

/**
 * P-6 Registro de prueba (Poligrafista). Siete pasos con los campos del documento ejecutivo. El
 * borrador se guarda solo cada 30 segundos y al cambiar de paso. La ortografia la marca el corrector
 * del navegador (los textos van con spellcheck en espanol); antes de enviar, la casilla «Revise la
 * ortografia» es obligatoria. Cada envio crea una version nueva del informe.
 */
const PASOS = ['Datos de la prueba', 'Evaluado', 'Historial', 'Entrevista', 'Admisiones', 'Preguntas', 'Revision y envio']
    .map((label) => ({ label }));
const VACIO = () => ({ historial: { primeraPrueba: null, fechaUltima: '' }, entrevista: {}, admisiones: [''], preguntas: [{ pregunta: '', respuesta: '', resultado: null }], adicionales: {} });
const RESULTADOS = [{ label: 'Paso', value: 'PASO' }, { label: 'No paso', value: 'NO_PASO' }];
const SI_NO = [{ label: 'Si', value: true }, { label: 'No', value: false }];

const Dato = ({ t, v }) => (
    <div className="col-12 md:col-6 lg:col-4">
        <div className="text-600 text-sm">{t}</div>
        <div className="font-medium">{v || '—'}</div>
    </div>
);

const CampoAdicional = ({ campo, valor, alCambiar }) => {
    const id = `campo-${campo.campo_id}`;
    let control;
    if (campo.tipo === 'LISTA') {
        control = <Dropdown inputId={id} value={valor || null} showClear options={(campo.opciones || '').split(';').map((o) => o.trim()).filter(Boolean)} onChange={(e) => alCambiar(e.value)} />;
    } else if (campo.tipo === 'SI_NO') {
        control = <SelectButton id={id} value={valor || null} options={['Si', 'No']} onChange={(e) => alCambiar(e.value)} />;
    } else if (campo.tipo === 'FECHA') {
        control = <InputText id={id} type="date" value={valor || ''} onChange={(e) => alCambiar(e.target.value)} />;
    } else if (campo.tipo === 'NUMERO') {
        control = <InputText id={id} keyfilter="num" value={valor || ''} onChange={(e) => alCambiar(e.target.value)} />;
    } else {
        control = <InputText id={id} value={valor || ''} spellCheck lang="es" onChange={(e) => alCambiar(e.target.value)} />;
    }
    return (
        <div className="field col-12 md:col-6 flex flex-column gap-1">
            <label htmlFor={id}>{campo.etiqueta}{campo.obligatorio ? ' *' : ''}</label>
            {control}
        </div>
    );
};

const Editor = ({ pruebaId, alCerrar, alEnviar }) => {
    const toast = useRef(null);
    const [d, setD] = useState(null);
    const [c, setC] = useState(VACIO());
    const [paso, setPaso] = useState(0);
    const [ortografia, setOrtografia] = useState(false);
    const [guardado, setGuardado] = useState(null);
    const [error, setError] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const sucio = useRef(false);
    const actual = useRef(c);
    actual.current = c;

    useEffect(() => {
        poli(`/pruebas/${pruebaId}/informe`).then((r) => {
            setD(r);
            if (r.contenido) setC({ ...VACIO(), ...r.contenido });
        }).catch((e) => setError(e.message));
    }, [pruebaId]);

    const guardar = useCallback(async () => {
        if (!sucio.current) return;
        try {
            await poli(`/pruebas/${pruebaId}/informe/borrador`, 'PUT', { contenido: actual.current });
            sucio.current = false;
            setGuardado(new Date());
        } catch (e) {
            setError(`No se guardo el borrador: ${e.message}`);
        }
    }, [pruebaId]);

    useEffect(() => {
        const t = setInterval(guardar, 30000);
        return () => { clearInterval(t); guardar(); };
    }, [guardar]);

    const cambiar = (fn) => { sucio.current = true; setC((x) => fn(JSON.parse(JSON.stringify(x)))); };
    const irA = (i) => { guardar(); setPaso(i); };

    const enviar = async () => {
        setOcupado(true);
        setError('');
        try {
            const r = await poli(`/pruebas/${pruebaId}/informe/enviar`, 'POST', { contenido: c, ortografiaConfirmada: ortografia });
            sucio.current = false;
            alEnviar(r.version);
        } catch (e) {
            setError(e.message);
        } finally {
            setOcupado(false);
        }
    };

    if (!d) return <Dialog header="Registro de prueba" visible onHide={alCerrar} style={{ width: 'min(70rem, 97vw)' }}>{error ? <Message severity="error" text={error} /> : 'Cargando…'}</Dialog>;
    const pr = d.prueba;
    const campos = (n) => (d.campos || []).filter((x) => x.paso === n).map((x) => (
        <CampoAdicional key={x.campo_id} campo={x} valor={c.adicionales[x.campo_id]} alCambiar={(v) => cambiar((y) => { y.adicionales[x.campo_id] = v; return y; })} />
    ));
    const pendientes = (d.observaciones || []).filter((o) => !o.atendida_version);

    return (
        <Dialog header={`Registro de prueba ${pr.codigo} · ${pr.apellidos} ${pr.nombres}`} visible onHide={() => { guardar(); alCerrar(); }}
            style={{ width: 'min(72rem, 98vw)' }} maximizable
            footer={(
                <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                    <small className="text-600">{guardado ? `Borrador guardado ${guardado.toLocaleTimeString('es-EC')}` : d.esBorrador ? 'Borrador recuperado' : 'Sin guardar'}</small>
                    <div className="flex gap-2">
                        <Button label="Anterior" icon="pi pi-arrow-left" className="p-button-text" disabled={paso === 0} onClick={() => irA(paso - 1)} />
                        {paso < 6 ? <Button label="Siguiente" icon="pi pi-arrow-right" iconPos="right" onClick={() => irA(paso + 1)} />
                            : <Button label="Enviar al repositorio" icon="pi pi-send" disabled={!ortografia} loading={ocupado} onClick={enviar} />}
                    </div>
                </div>
            )}>
            <Toast ref={toast} />
            <Steps model={PASOS} activeIndex={paso} onSelect={(e) => irA(e.index)} readOnly={false} className="mb-4" />
            {pendientes.length > 0 && (
                <Message className="mb-3 w-full justify-content-start" severity="warn"
                    text={`Gerencia devolvio el informe: ${pendientes.map((o) => o.texto).join(' / ')}`} />
            )}

            {paso === 0 && (
                <div className="grid">
                    <Dato t="Codigo unico" v={pr.codigo} />
                    <Dato t="Tipo de prueba" v={pr.tipo_prueba} />
                    <Dato t="Ciudad y fecha" v={`${pr.ciudad || ''} ${fechaHora(pr.fecha_asignada)}`} />
                    <div className="col-12"><ArchivosPrueba pruebaId={pr.prueba_id} puedeAdjuntar={false} /></div>
                    {campos(1)}
                </div>
            )}
            {paso === 1 && (
                <div className="grid">
                    <Dato t="Nombres y apellidos" v={`${pr.nombres} ${pr.apellidos}`} />
                    <Dato t="Identificacion" v={pr.cedula} />
                    <Dato t="Compania" v={pr.compania} />
                    <Dato t="Proyecto o division" v={pr.proyecto} />
                    <Dato t="Fecha de ingreso" v={pr.fecha_ingreso && new Date(pr.fecha_ingreso + 'T12:00').toLocaleDateString('es-EC')} />
                    <Dato t="Puesto" v={pr.puesto} />
                    {campos(2)}
                </div>
            )}
            {paso === 2 && (
                <div className="grid formgrid">
                    <div className="field col-12 md:col-6 flex flex-column gap-2">
                        <label htmlFor="hist-primera">¿Es su primera prueba de poligrafia?</label>
                        <SelectButton id="hist-primera" value={c.historial.primeraPrueba} options={SI_NO}
                            onChange={(e) => cambiar((y) => { y.historial.primeraPrueba = e.value; return y; })} />
                    </div>
                    {c.historial.primeraPrueba === false && (
                        <div className="field col-12 md:col-6 flex flex-column gap-2">
                            <label htmlFor="hist-fecha">Fecha de la ultima prueba</label>
                            <InputText id="hist-fecha" type="date" value={c.historial.fechaUltima || ''}
                                onChange={(e) => cambiar((y) => { y.historial.fechaUltima = e.target.value; return y; })} />
                        </div>
                    )}
                    {campos(3)}
                </div>
            )}
            {paso === 3 && (
                <>
                    <TabView scrollable>
                        {TEMAS_ENTREVISTA.map((t) => (
                            <TabPanel key={t.clave} header={t.titulo}>
                                <InputTextarea id={`ent-${t.clave}`} aria-label={t.titulo} rows={10} autoResize spellCheck lang="es" className="w-full"
                                    value={c.entrevista[t.clave] || ''} onChange={(e) => cambiar((y) => { y.entrevista[t.clave] = e.target.value; return y; })} />
                            </TabPanel>
                        ))}
                    </TabView>
                    <div className="grid formgrid">{campos(4)}</div>
                </>
            )}
            {paso === 4 && (
                <div className="flex flex-column gap-2">
                    {c.admisiones.map((a, i) => (
                        <div key={i} className="flex gap-2 align-items-start">
                            <b className="mt-2" style={{ width: '1.5rem' }}>{String.fromCharCode(65 + i)}.</b>
                            <InputTextarea aria-label={`Admision ${String.fromCharCode(65 + i)}`} rows={2} autoResize spellCheck lang="es" className="flex-1" value={a}
                                onChange={(e) => cambiar((y) => { y.admisiones[i] = e.target.value; return y; })} />
                            <Button icon="pi pi-times" className="p-button-text p-button-danger" aria-label="Quitar admision" disabled={c.admisiones.length === 1}
                                onClick={() => cambiar((y) => { y.admisiones.splice(i, 1); return y; })} />
                        </div>
                    ))}
                    <Button label="Agregar parrafo" icon="pi pi-plus" className="p-button-text align-self-start" onClick={() => cambiar((y) => { y.admisiones.push(''); return y; })} />
                    <div className="grid formgrid">{campos(5)}</div>
                </div>
            )}
            {paso === 5 && (
                <div className="flex flex-column gap-3">
                    {c.preguntas.map((q, i) => (
                        <div key={i} className="border-1 surface-border border-round p-3 flex flex-column gap-2">
                            <div className="flex justify-content-between"><b>Pregunta {i + 1}</b>
                                <Button icon="pi pi-times" className="p-button-text p-button-danger p-button-sm" aria-label="Quitar pregunta" disabled={c.preguntas.length === 1}
                                    onClick={() => cambiar((y) => { y.preguntas.splice(i, 1); return y; })} />
                            </div>
                            <Dropdown value={q.pregunta} editable options={d.preguntas || []} placeholder="Elija del banco o escriba la pregunta" aria-label={`Pregunta ${i + 1}`}
                                onChange={(e) => cambiar((y) => { y.preguntas[i].pregunta = e.value || ''; return y; })} />
                            <InputText value={q.respuesta} placeholder="Respuesta" aria-label={`Respuesta ${i + 1}`} spellCheck lang="es"
                                onChange={(e) => cambiar((y) => { y.preguntas[i].respuesta = e.target.value; return y; })} />
                            <SelectButton value={q.resultado} options={RESULTADOS} aria-label={`Resultado ${i + 1}`}
                                onChange={(e) => cambiar((y) => { y.preguntas[i].resultado = e.value; return y; })} />
                        </div>
                    ))}
                    <Button label="Agregar pregunta" icon="pi pi-plus" className="p-button-text align-self-start"
                        onClick={() => cambiar((y) => { y.preguntas.push({ pregunta: '', respuesta: '', resultado: null }); return y; })} />
                    <div className="grid formgrid">{campos(6)}</div>
                </div>
            )}
            {paso === 6 && (
                <div className="flex flex-column gap-3">
                    <div className="grid formgrid">{campos(7)}</div>
                    <ul className="m-0 pl-4 line-height-3">
                        <li>Historial: {c.historial.primeraPrueba === null ? <b className="text-red-600">sin responder</b> : c.historial.primeraPrueba ? 'primera prueba' : `ultima prueba ${c.historial.fechaUltima || '(falta la fecha)'}`}</li>
                        <li>Entrevista: {TEMAS_ENTREVISTA.filter((t) => (c.entrevista[t.clave] || '').trim()).length} de 8 temas con texto</li>
                        <li>Admisiones: {c.admisiones.filter((a) => a.trim()).length}</li>
                        <li>Preguntas completas: {c.preguntas.filter((q) => q.pregunta.trim() && q.respuesta.trim() && q.resultado).length} de {c.preguntas.length}</li>
                    </ul>
                    <div className="flex align-items-center gap-2">
                        <Checkbox inputId="rev-ortografia" checked={ortografia} onChange={(e) => setOrtografia(e.checked)} />
                        <label htmlFor="rev-ortografia"><b>Revise la ortografia del informe</b> (las palabras subrayadas en rojo por el navegador)</label>
                    </div>
                    <small className="text-600">Al enviar se crea la version {(d.versiones || []).length + 1} del informe y la prueba pasa a «Informe registrado». Las versiones anteriores no se modifican.</small>
                </div>
            )}
            {error && <Message className="mt-3 w-full justify-content-start" severity="error" text={error} />}
        </Dialog>
    );
};

const PoliRegistro = () => {
    const toast = useRef(null);
    const [filas, setFilas] = useState([]);
    const [abierta, setAbierta] = useState(null);
    const [cargando, setCargando] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            setFilas(await poli('/registro/pendientes'));
        } catch (e) {
            toast.current && toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between mb-3">
                <h4 className="m-0">Registro de prueba</h4>
                <Button label="Actualizar" icon="pi pi-refresh" className="p-button-outlined" onClick={cargar} loading={cargando} />
            </div>
            <DataTable value={filas} size="small" rowHover dataKey="prueba_id" onRowClick={(e) => setAbierta(e.data.prueba_id)} style={{ cursor: 'pointer' }}
                emptyMessage="No tiene pruebas por registrar" responsiveLayout="scroll">
                <Column header="Colaborador" body={(r) => <div><b>{r.apellidos} {r.nombres}</b><br /><small className="text-600">{r.cedula}</small></div>} />
                <Column header="Compania" body={(r) => <div>{r.compania}<br /><small className="text-600">{r.proyecto}</small></div>} />
                <Column header="Cita" body={(r) => fechaHora(r.fecha_asignada)} />
                <Column header="Pendiente" field="motivo_pendiente" />
                <Column header="Estado" body={(r) => <EstadoTag fila={r} />} />
                <Column field="codigo" header="Codigo" />
            </DataTable>
            {abierta && (
                <Editor pruebaId={abierta} alCerrar={() => { setAbierta(null); cargar(); }}
                    alEnviar={(v) => { setAbierta(null); toast.current && toast.current.show({ severity: 'success', summary: 'Informe enviado', detail: `Version ${v} registrada`, life: 4000 }); cargar(); }} />
            )}
        </div>
    );
};

export default PoliRegistro;
